import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, ShieldCheck } from "lucide-react";
import { trackWhoami, trackIngest } from "../lib/api";

export default function LiveShare() {
  const token = new URLSearchParams(window.location.search).get("t") || "";
  const [name, setName] = useState("");
  const [status, setStatus] = useState("init"); // init|ready|sharing|denied|error|invalid
  const [count, setCount] = useState(0);
  const lastSent = useRef(0);
  const watchId = useRef(null);

  useEffect(() => {
    (async () => {
      if (!token) { setStatus("invalid"); return; }
      try { const r = await trackWhoami(token); setName(r.name || ""); setStatus("ready"); }
      catch (e) { setStatus("invalid"); }
    })();
  }, [token]);

  const start = () => {
    if (!navigator.geolocation) { setStatus("error"); return; }
    setStatus("sharing");
    watchId.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const now = Date.now();
        if (now - lastSent.current < 12000) return; // throttle ~12s
        lastSent.current = now;
        try {
          await trackIngest({ token, lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy, source: "web" });
          setCount((c) => c + 1);
        } catch (e) { /* keep trying */ }
      },
      (err) => { setStatus(err.code === 1 ? "denied" : "error"); },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
  };

  useEffect(() => () => { if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current); }, []);

  return (
    <div className="min-h-screen bg-[#030712] text-cyan-50 flex flex-col items-center justify-center p-6 text-center" style={{ fontFamily: "system-ui, sans-serif" }}>
      <MapPin className="w-14 h-14 text-[#00F0FF] mb-4" />
      {status === "invalid" && <p className="text-rose-300 font-semibold">This tracking link is invalid or expired.</p>}
      {status === "init" && <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />}
      {status === "ready" && (
        <>
          <h1 className="text-2xl font-bold">Hi {name || "there"} 👋</h1>
          <p className="text-cyan-200/60 mt-2 max-w-sm">Uncle Nexus would like to know where you are so he can look out for you. Tap below to share your location.</p>
          <button onClick={start} data-testid="liveshare-start"
            className="mt-6 px-6 py-3 rounded-full font-bold text-[#030712] bg-[#00F0FF] hover:bg-[#5ff7ff]">Share my location</button>
          <p className="text-cyan-200/40 text-xs mt-4 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Only shared while this page is open.</p>
        </>
      )}
      {status === "sharing" && (
        <>
          <h1 className="text-2xl font-bold text-emerald-400">Sharing ✓</h1>
          <p className="text-cyan-200/60 mt-2 max-w-sm">Your location is being shared with Uncle Nexus. Keep this page open. ({count} update{count === 1 ? "" : "s"} sent)</p>
          <div className="mt-4 w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
        </>
      )}
      {status === "denied" && <p className="text-amber-300 mt-2 max-w-sm">Location permission was blocked. Please enable location for this site in your browser settings, then reload.</p>}
      {status === "error" && <p className="text-rose-300 mt-2">Couldn't access location on this device.</p>}
    </div>
  );
}
